/**
 * Context provider for permission-based access control.
 *
 * Wraps the `usePermissions` hook in a React context so all components
 * can access `hasPermission(key)` without redundant API calls.
 *
 * Mount inside `AuthProvider` but above the route tree.
 *
 * @module store/PermissionContext
 * @see ARCH-005-MOD-005
 */
import React, { createContext, useContext } from "react";
import { usePermissions, type UsePermissionsResult } from "../hooks/usePermissions";
import { useAuth } from "./AuthContext";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const PermissionContext = createContext<UsePermissionsResult | undefined>(
  undefined,
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PermissionProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { authenticated, accessToken } = useAuth();
  const value = usePermissions(authenticated, accessToken);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the permission context.  Must be called within a `PermissionProvider`.
 *
 * Returns `{ permissions, hasPermission, loading }`.
 */
export function usePermissionContext(): UsePermissionsResult {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error(
      "usePermissionContext must be used within a PermissionProvider",
    );
  }
  return ctx;
}
